import { useEffect, useRef, useCallback, useState } from 'react';
import type { TelegramMessage, PublicMessage, ChatUpdate, TypingEvent, UserStatusEvent, ReadHistoryEvent } from '../types';
import { resolveWsUrl } from '@/providers/api-url';

interface TelegramWsMessage {
  type: string;
  data?: {
    chatId?: number;
    telegramChatId?: number;
    message?: TelegramMessage | PublicMessage;
    messageIds?: number[];
    userId?: number;
    action?: string;
    status?: string;
    lastSeen?: string;
    maxId?: number;
    direction?: string;
    // Chat update fields
    lastMessage?: string | null;
    lastMessageDate?: string | null;
    unreadCount?: number;
    readOutboxMaxId?: number;
  };
}

interface UseTelegramWebSocketOptions {
  enabled?: boolean;
  onNewMessage?: (chatId: number, message: TelegramMessage) => void;
  onMessageEdited?: (chatId: number, message: TelegramMessage) => void;
  onMessagesDeleted?: (chatId: number, messageIds: number[]) => void;
  onPublicMessage?: (message: PublicMessage) => void;
  onChatUpdate?: (update: ChatUpdate) => void;
  onTyping?: (event: TypingEvent) => void;
  onUserStatus?: (event: UserStatusEvent) => void;
  onReadHistory?: (event: ReadHistoryEvent) => void;
  onSessionExpired?: () => void;
}

// В dev — напрямую к backend (Vite proxy для WS не используется этим хуком);
// в prod URL выводится из VITE_WS_URL/VITE_API_URL, без localhost-фолбэка.
const WS_URL = import.meta.env.DEV ? 'ws://localhost:3000' : resolveWsUrl();

export function useTelegramWebSocket(options: UseTelegramWebSocketOptions = {}) {
  const {
    enabled = true, onNewMessage, onMessageEdited, onMessagesDeleted,
    onPublicMessage, onChatUpdate, onTyping, onUserStatus, onReadHistory,
    onSessionExpired,
  } = options;
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<'connected' | 'connecting' | 'disconnected'>('disconnected');
  const subscribedChats = useRef<Set<number>>(new Set());
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectRef = useRef<(() => void) | undefined>(undefined);

  // Используем refs для колбеков чтобы избежать пересоздания connect при их изменении
  const onNewMessageRef = useRef(onNewMessage);
  const onMessageEditedRef = useRef(onMessageEdited);
  const onMessagesDeletedRef = useRef(onMessagesDeleted);
  const onPublicMessageRef = useRef(onPublicMessage);
  const onChatUpdateRef = useRef(onChatUpdate);
  const onTypingRef = useRef(onTyping);
  const onUserStatusRef = useRef(onUserStatus);
  const onReadHistoryRef = useRef(onReadHistory);
  const onSessionExpiredRef = useRef(onSessionExpired);

  // Обновляем refs при изменении колбеков
  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    onMessageEditedRef.current = onMessageEdited;
  }, [onMessageEdited]);

  useEffect(() => {
    onMessagesDeletedRef.current = onMessagesDeleted;
  }, [onMessagesDeleted]);

  useEffect(() => {
    onPublicMessageRef.current = onPublicMessage;
  }, [onPublicMessage]);

  useEffect(() => {
    onChatUpdateRef.current = onChatUpdate;
  }, [onChatUpdate]);

  useEffect(() => {
    onTypingRef.current = onTyping;
  }, [onTyping]);

  useEffect(() => {
    onUserStatusRef.current = onUserStatus;
  }, [onUserStatus]);

  useEffect(() => {
    onReadHistoryRef.current = onReadHistory;
  }, [onReadHistory]);

  useEffect(() => {
    onSessionExpiredRef.current = onSessionExpired;
  }, [onSessionExpired]);

  const connect = useCallback(() => {
    if (!enabled) {return;}

    const token = localStorage.getItem('accessToken');
    if (!token) {return;}

    // Не создаём новое соединение если уже есть активное
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    try {
      // WebSocket endpoint находится в /api/ws/telegram (часть apiControllers)
      wsRef.current = new WebSocket(`${WS_URL}/api/ws/telegram?token=${token}`);
      setConnectionState('connecting');

      wsRef.current.onopen = () => {
        setIsConnected(true);
        setConnectionState('connected');
        // Re-subscribe to previously subscribed chats
        subscribedChats.current.forEach(chatId => {
          wsRef.current?.send(JSON.stringify({ type: 'subscribe_chat', chatId }));
        });
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data: TelegramWsMessage = JSON.parse(event.data);

          switch (data.type) {
            case 'telegram_new_message':
              if (data.data?.chatId && data.data?.message) {
                onNewMessageRef.current?.(data.data.chatId, data.data.message as TelegramMessage);
              }
              break;

            case 'telegram_message_edited':
              if (data.data?.chatId && data.data?.message) {
                onMessageEditedRef.current?.(data.data.chatId, data.data.message as TelegramMessage);
              }
              break;

            case 'telegram_messages_deleted':
              if (data.data?.messageIds) {
                onMessagesDeletedRef.current?.(data.data.chatId ?? 0, data.data.messageIds);
              }
              break;

            case 'telegram_public_message':
              if (data.data?.message) {
                onPublicMessageRef.current?.(data.data.message as PublicMessage);
              }
              break;

            case 'telegram_chat_updated':
              if (data.data?.chatId) {
                onChatUpdateRef.current?.({
                  chatId: data.data.chatId,
                  telegramChatId: data.data.telegramChatId ?? 0,
                  lastMessage: data.data.lastMessage ?? undefined,
                  lastMessageDate: data.data.lastMessageDate ? new Date(data.data.lastMessageDate) : undefined,
                  unreadCount: data.data.unreadCount,
                  readOutboxMaxId: data.data.readOutboxMaxId,
                });
              }
              break;

            case 'telegram_typing':
              if (data.data?.chatId && data.data?.userId) {
                onTypingRef.current?.({
                  chatId: data.data.chatId,
                  userId: data.data.userId,
                  action: (data.data.action ?? 'typing') as TypingEvent['action'],
                });
              }
              break;

            case 'telegram_user_status':
              if (data.data?.userId) {
                onUserStatusRef.current?.({
                  userId: data.data.userId,
                  status: (data.data.status ?? 'offline') as UserStatusEvent['status'],
                  lastSeen: data.data.lastSeen,
                });
              }
              break;

            case 'telegram_read_history':
              if (data.data?.chatId && data.data?.maxId !== undefined) {
                onReadHistoryRef.current?.({
                  chatId: data.data.chatId,
                  maxId: data.data.maxId,
                  direction: (data.data.direction ?? 'inbox') as ReadHistoryEvent['direction'],
                });
              }
              break;

            case 'telegram_session_expired':
              onSessionExpiredRef.current?.();
              break;

            case 'ping':
              wsRef.current?.send(JSON.stringify({ type: 'pong' }));
              break;
          }
        } catch (e) {
          console.error('[TelegramWS] Parse error:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        setIsConnected(false);
        setConnectionState('disconnected');
        // Не переподключаемся при ошибках авторизации (4001-4003)
        if (event.code >= 4001 && event.code <= 4003) {
          console.warn('[TelegramWS] Auth error, not reconnecting:', event.code, event.reason);
          return;
        }
        // Attempt reconnect after 5 seconds for other errors
        setConnectionState('connecting');
        reconnectTimeoutRef.current = setTimeout(() => connectRef.current?.(), 5000);
      };

      wsRef.current.onerror = () => {
        // Не логируем ошибки - они уже обрабатываются в onclose
      };

    } catch (error) {
      console.error('[TelegramWS] Connection error:', error);
    }
  }, [enabled]); // Убрали колбеки из зависимостей - используем refs

  // Сохраняем функцию connect в ref для использования в setTimeout
  connectRef.current = connect;

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const subscribeToChat = useCallback((chatId: number) => {
    subscribedChats.current.add(chatId);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe_chat', chatId }));
    }
  }, []);

  const unsubscribeFromChat = useCallback((chatId: number) => {
    subscribedChats.current.delete(chatId);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe_chat', chatId }));
    }
  }, []);

  const sendTyping = useCallback((chatId: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', chatId }));
    }
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionState,
    subscribeToChat,
    unsubscribeFromChat,
    sendTyping,
    reconnect: connect,
  };
}
